import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams, useParams } from 'react-router-dom';
import axios from 'axios';
import { parseTags, getAllTags, getTagCounts, getMajorCategoryCounts, designMatchesTag, getCategoryColor, isNotForSale, sortTagsAlphabetically } from '../utils/tagUtils';
import { trackTagClick, trackCategoryClick, trackProductClick } from '../utils/visitorTracking';

import { API_BASE_URL } from '../config/api';
import { fetchCategories } from '../utils/categories';

function GalleryPage() {
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [orientationFilter, setOrientationFilter] = useState(null); // null = all, 'horizontal', 'vertical'
  const [activePromos, setActivePromos] = useState([]);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const params = useParams();
  const [selectedTags, setSelectedTags] = useState([]);
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [majorCategories, setMajorCategories] = useState([]);
  const navigate = useNavigate();

  // Derived single tag, used by the category sub-tag panel (null when 0 or 2+ tags selected)
  const selectedTag = selectedTags.length === 1 ? selectedTags[0] : null;
  const tagsParam = params.tags;

  // Pretty tag URL builder: /t/floral+red+orchids; an empty set maps to /t/all ("show everything")
  const buildTagPath = (tags) => {
    const cleaned = (Array.isArray(tags) ? tags : []).filter(Boolean);
    if (cleaned.length === 0) {
      return '/t/all';
    }
    return `/t/${cleaned.map((t) => encodeURIComponent(t)).join('+')}`;
  };
  
  // Check if user is logged in (use localStorage to work across tabs)
  useEffect(() => {
    const checkLogin = async () => {
      // Check localStorage (shared across tabs) first, then sessionStorage as fallback
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      if (token) {
        // Verify token is still valid with server
        try {
          const response = await axios.get(`${API_BASE_URL}/auth/verify`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setIsLoggedIn(response.data.success);
        } catch (err) {
          // Token invalid or expired
          setIsLoggedIn(false);
          localStorage.removeItem('authToken');
          localStorage.removeItem('username');
          sessionStorage.removeItem('authToken');
          sessionStorage.removeItem('username');
        }
      } else {
        setIsLoggedIn(false);
      }
    };
    
    // Check on mount
    checkLogin();
    
    // Listen for storage changes (when login happens in another tab)
    const handleStorageChange = (e) => {
      if (e.key === 'authToken' || e.key === null) {
        checkLogin();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    
    // Re-check when window/tab gains focus
    const handleFocus = () => {
      checkLogin();
    };
    window.addEventListener('focus', handleFocus);
    
    // Also re-check periodically (every 3 seconds) as backup
    const interval = setInterval(checkLogin, 3000);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);


  // Fetch active promos
  useEffect(() => {
    const fetchPromos = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/promos`);
        if (response.data.success) {
          setActivePromos(response.data.data || []);
        }
      } catch (err) {
        console.error('Error loading promos:', err);
      }
    };
    fetchPromos();
  }, []);

  useEffect(() => {
    const fetchDesigns = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`${API_BASE_URL}/designs`);
        if (response.data.success) {
          setDesigns(response.data.data);
        } else {
          setError('Failed to load designs');
        }
      } catch (err) {
        console.error('Error fetching designs:', err);
        setError(err.response?.data?.error || err.message || 'Failed to load designs');
      } finally {
        setLoading(false);
      }
    };

    fetchDesigns();
    // Load categories
    fetchCategories().then(categories => {
      setMajorCategories(categories.categories.map(c => c.name));
    });
  }, []);

  // Resolve the active tag filter from the URL.
  // Priority: pretty path (/t/a+b) > legacy query (?tag=a or ?category=a) > nav state
  useEffect(() => {
    let tags = [];

    if (tagsParam) {
      // Pretty path form: /t/floral+red+orchids (spaces must be %20-encoded)
      tags = tagsParam.split('+');
    } else {
      const queryTag = searchParams.get('tag') || searchParams.get('category');
      if (queryTag) {
        // Legacy form: /?tag=floral or /?tag=floral,red
        tags = queryTag.split(',');
      } else if (location.state?.selectedTag) {
        tags = [location.state.selectedTag];
      }
    }

    // "all" is a sentinel meaning "no filter" (show the full gallery)
    tags = tags
      .map((t) => (typeof t === 'string' ? t.trim() : ''))
      .filter((t) => t && t.toLowerCase() !== 'all');

    setSelectedTags(tags);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagsParam, searchParams, location.state]);

  const handleDesignClick = (designId) => {
    // Track product click
    const design = designs.find(d => d.id === designId);
    if (design) {
      trackProductClick(designId, design.title);
    }
    navigate(`/design/${designId}`);
  };

  const handleTagClick = (tag, e) => {
    if (e) {
      e.stopPropagation(); // Prevent card click
    }
    // Track tag click
    trackTagClick(tag);
    const nextTags = selectedTags.includes(tag)
      ? selectedTags.filter((t) => t !== tag)
      : [...selectedTags, tag];
    setSearchQuery(''); // Clear search when filtering by tag
    navigate(buildTagPath(nextTags));
  };

  const handleCategoryClick = (category) => {
    // Track category click
    trackCategoryClick(category);
    setSearchQuery(''); // Clear search when filtering by category
    navigate(buildTagPath([category]));
  };

  const handleClearAllFilters = () => {
    setSearchQuery('');
    setOrientationFilter(null);
    navigate('/t/all');
  };

  // Get tag statistics
  const tagCounts = useMemo(() => getTagCounts(designs), [designs]);
  const majorCategoryCounts = useMemo(() => 
    getMajorCategoryCounts(designs, majorCategories), 
    [designs, majorCategories]
  );
  
  // Get popular tags (top 10)
  const popularTags = useMemo(() => {
    const topTags = Object.entries(tagCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([tag]) => tag);
    return sortTagsAlphabetically(topTags);
  }, [tagCounts]);

  // Get all tags in the selected category
  const categoryTags = useMemo(() => {
    if (!selectedTag) {
      return [];
    }
    
    // Check if selectedTag is a major category (case-insensitive comparison)
    const isMajorCategory = majorCategories.some(cat => 
      cat.toLowerCase() === selectedTag.toLowerCase()
    );
    
    if (!isMajorCategory) {
      return [];
    }
    
    // Get all designs with this major category (case-insensitive)
    const selectedCategoryLower = selectedTag.toLowerCase();
    const categoryDesigns = designs.filter(design => {
      if (!design.tags) return false;
      const { majorCategory } = parseTags(design.tags);
      return majorCategory && majorCategory.toLowerCase() === selectedCategoryLower;
    });
    
    // Extract all minor tags from these designs
    const tagCountsInCategory = {};
    categoryDesigns.forEach(design => {
      if (design.tags) {
        const { minorTags } = parseTags(design.tags);
        minorTags.forEach(tag => {
          tagCountsInCategory[tag] = (tagCountsInCategory[tag] || 0) + 1;
        });
      }
    });
    
    // Return sorted by count
    return Object.entries(tagCountsInCategory)
      .sort((a, b) =>
        a[0].toLowerCase().localeCompare(b[0].toLowerCase(), undefined, { sensitivity: 'base' })
      )
      .map(([tag, count]) => ({ tag, count }));
  }, [designs, selectedTag, majorCategories]);

  // Filter designs based on search query, orientation, and tags
  const filteredDesigns = useMemo(() => {
    let filtered = designs;

    // "loved" is a special filter: designs with loves > 0, sorted by love count
    const hasLoved = selectedTags.includes('loved');
    const regularTags = selectedTags.filter((t) => t !== 'loved');

    if (hasLoved) {
      filtered = filtered.filter((design) => (design.loves || 0) > 0);
      // Sort by love count descending
      filtered = filtered.sort((a, b) => (b.loves || 0) - (a.loves || 0));
    }

    // Filter by orientation
    if (orientationFilter) {
      filtered = filtered.filter((design) => {
        const designOrientation = (design.orientation || 'horizontal').toLowerCase();
        return designOrientation === orientationFilter.toLowerCase();
      });
    }

    // Filter by tags (AND semantics: a design must match every selected tag)
    if (regularTags.length > 0) {
      filtered = filtered.filter((design) =>
        regularTags.every((tag) => designMatchesTag(design, tag))
      );
    }

    // Filter by search query (includes tags)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((design) => {
        const title = (design.title || '').toLowerCase();
        const description = (design.description || '').toLowerCase();
        const tags = design.tags ? getAllTags(design.tags).join(' ').toLowerCase() : '';
        return title.includes(query) || description.includes(query) || tags.includes(query);
      });
    }

    return filtered;
  }, [designs, searchQuery, orientationFilter, selectedTags]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading designs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-red-800 mb-2">Error</h2>
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 lg:py-12 px-4 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Promo Sections */}
        {activePromos.length > 0 && (
          <div className="mb-6 md:mb-8 lg:mb-12 space-y-4">
            {activePromos.map((promo) => (
              <div
                key={promo.id}
                className="promo-section"
                dangerouslySetInnerHTML={{ __html: promo.content }}
              />
            ))}
          </div>
        )}
        
        <header className="mb-6 md:mb-8 lg:mb-12">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 md:gap-4 mb-3 md:mb-4">
            <p className="text-base md:text-lg text-gray-600">
              Choose a design and customize it for your print format
            </p>
            <p className="text-xs md:text-sm text-gray-500">
              {searchQuery.trim() || orientationFilter || selectedTags.length > 0
                ? (
                    <>
                      Showing {filteredDesigns.length} of{' '}
                      <button
                        onClick={handleClearAllFilters}
                        className="text-blue-600 hover:text-blue-700 underline font-medium"
                      >
                        {designs.length} designs
                      </button>
                    </>
                  )
                : (
                    <>
                      <button
                        onClick={handleClearAllFilters}
                        className="text-blue-600 hover:text-blue-700 underline font-medium"
                      >
                        {designs.length} design{designs.length !== 1 ? 's' : ''}
                      </button>
                      {' '}available
                    </>
                  )
              }
            </p>
          </div>
          
          {/* Search Box and Filters */}
          <div className="mb-4 md:mb-6 flex flex-col sm:flex-row gap-3 md:gap-4 items-start sm:items-center">
            <div className="relative flex-1 w-full sm:max-w-md">
              <input
                type="text"
                placeholder="Search by title, description, or tags..."
                value={searchQuery}
                onChange={(e) => {
                  const newQuery = e.target.value;
                  setSearchQuery(newQuery);
                  // Clear tag filters when the user starts searching
                  if (newQuery.trim() && selectedTags.length > 0) {
                    setSelectedTags([]);
                    navigate('/t/all', { replace: true });
                  }
                }}
                className="w-full px-3 py-2 md:px-4 md:py-3 pl-9 md:pl-10 pr-4 text-sm md:text-base text-gray-700 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-2.5 md:pl-3 pointer-events-none">
                <svg
                  className="w-4 h-4 md:w-5 md:h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-0 flex items-center pr-2.5 md:pr-3 text-gray-400 hover:text-gray-600"
                >
                  <svg
                    className="w-4 h-4 md:w-5 md:h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
            
            {/* Orientation Filter */}
            <div className="flex gap-1.5 md:gap-2 w-full sm:w-auto">
              <button
                onClick={() => setOrientationFilter(null)}
                className={`flex-1 sm:flex-none px-2 py-1.5 md:px-4 md:py-2 text-xs md:text-sm font-medium rounded-lg transition-colors ${
                  orientationFilter === null
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setOrientationFilter('horizontal')}
                className={`flex-1 sm:flex-none px-2 py-1.5 md:px-4 md:py-2 text-xs md:text-sm font-medium rounded-lg transition-colors ${
                  orientationFilter === 'horizontal'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Horizontal
              </button>
              <button
                onClick={() => setOrientationFilter('vertical')}
                className={`flex-1 sm:flex-none px-2 py-1.5 md:px-4 md:py-2 text-xs md:text-sm font-medium rounded-lg transition-colors ${
                  orientationFilter === 'vertical'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Vertical
              </button>
            </div>
          </div>

          {/* Active Filters */}
          {(selectedTags.length > 0 || searchQuery.trim()) && (
            <div className="mb-3 md:mb-4 flex flex-wrap gap-1.5 md:gap-2 items-center">
              <span className="text-xs md:text-sm text-gray-600">Active:</span>
              {selectedTags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => handleTagClick(tag)}
                  className="inline-flex items-center gap-1 px-2 py-0.5 md:px-3 md:py-1 bg-blue-100 text-blue-800 rounded-full text-xs md:text-sm font-medium hover:bg-blue-200"
                >
                  <span className="max-w-[120px] md:max-w-none truncate">
                    {tag === 'loved' ? (
                      <span className="inline-flex items-center gap-1">
                        <svg className="w-4 h-4 text-slate-500" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                        </svg>
                        Loved Items
                      </span>
                    ) : tag}
                  </span>
                  <svg className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              ))}
              {searchQuery.trim() && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="inline-flex items-center gap-1 px-2 py-0.5 md:px-3 md:py-1 bg-gray-100 text-gray-800 rounded-full text-xs md:text-sm font-medium hover:bg-gray-200"
                >
                  <span className="max-w-[100px] md:max-w-none truncate">"{searchQuery}"</span>
                  <svg className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          )}

          {/* Category Filters - Collapsible on Mobile */}
          <div className="mb-4 md:mb-6">
            {/* Mobile Toggle Button */}
            <div className="md:hidden mb-3">
              <button
                onClick={() => setShowFiltersMobile(!showFiltersMobile)}
                className="w-full flex items-center justify-between px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <span className="text-sm font-medium">
                  {showFiltersMobile ? 'Hide Filters' : 'Show Filters'}
                </span>
                <svg
                  className={`w-5 h-5 transition-transform ${showFiltersMobile ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>

            {/* Filters Content - Hidden on mobile unless expanded */}
            <div className={`${showFiltersMobile ? 'block' : 'hidden'} md:block`}>
              <div className="mb-3">
                <p className="text-xs md:text-sm font-medium text-gray-700 mb-2">Filter by Category:</p>
                <div className="flex flex-wrap gap-1.5 md:gap-2">
                  {Object.entries(majorCategoryCounts)
                    .sort((a, b) => b[1] - a[1])
                    .map(([category, count]) => (
                      <button
                        key={category}
                        onClick={() => handleCategoryClick(category)}
                        className={`px-2 py-1 md:px-3 md:py-1 text-xs font-medium rounded-lg transition-colors ${
                          selectedTags.includes(category)
                            ? getCategoryColor(category) + ' border-2'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                        }`}
                      >
                        <span className="hidden sm:inline">{category}</span>
                        <span className="sm:hidden">{category.split(' ')[0]}</span>
                        <span className="ml-1">({count})</span>
                      </button>
                    ))}
                </div>
              </div>
              
              {/* Show category tags when a category is selected, otherwise show popular tags */}
              {selectedTag && majorCategories.some(cat => cat.toLowerCase() === selectedTag.toLowerCase()) ? (
                categoryTags.length > 0 ? (
                  <div>
                    <p className="text-xs md:text-sm font-medium text-gray-700 mb-2">
                      Tags in "{selectedTag}":
                    </p>
                    <div className="flex flex-wrap gap-1.5 md:gap-2">
                      {categoryTags.map(({ tag, count }) => (
                        <button
                          key={tag}
                          onClick={(e) => handleTagClick(tag, e)}
                          className="px-2 py-0.5 md:px-3 md:py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
                        >
                          {tag} ({count})
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null
              ) : (
                selectedTags.length === 0 && popularTags.length > 0 && (
                  <div>
                    <p className="text-xs md:text-sm font-medium text-gray-700 mb-2">Popular Tags:</p>
                    <div className="flex flex-wrap gap-1.5 md:gap-2">
                      {popularTags.map((tag) => (
                        <button
                          key={tag}
                          onClick={(e) => handleTagClick(tag, e)}
                          className="px-2 py-0.5 md:px-3 md:py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
                        >
                          {tag} ({tagCounts[tag]})
                        </button>
                      ))}
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </header>

        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
          {filteredDesigns.map((design) => {
            const parsedTags = parseTags(design.tags || '');
            const { majorCategory, minorTags } = parsedTags;
            // Ensure minorTags is always an array
            const safeMinorTags = sortTagsAlphabetically(Array.isArray(minorTags) ? minorTags : []);
            const notForSale = isNotForSale(design);
            return (
              <div
                key={design.id}
                onClick={() => handleDesignClick(design.id)}
                className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow cursor-pointer overflow-hidden"
              >
                <div className="aspect-square bg-gray-100 overflow-hidden relative group">
                  <img
                    src={`${API_BASE_URL.replace('/api', '')}/api/designs/${design.id}/thumbnail`}
                    alt={design.title}
                    width="400"
                    height="400"
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      // Fallback to original thumbnail if endpoint fails
                      e.target.src = design.thumbnail_url;
                    }}
                  />
                  {/* NOT FOR SALE Badge - Top right */}
                  {notForSale && (
                    <div className="absolute top-1 right-1 md:top-2 md:right-2 px-1.5 py-0.5 md:px-2 md:py-1 rounded text-xs font-medium bg-amber-500 text-white border border-amber-600 shadow-md z-10">
                      <span className="hidden sm:inline">Display Only</span>
                      <span className="sm:hidden">Display</span>
                    </div>
                  )}
                  {/* Major Category Badge - Top left */}
                  {majorCategory && (
                    <div className={`absolute top-1 left-1 md:top-2 md:left-2 px-1.5 py-0.5 md:px-2 md:py-1 rounded text-xs font-medium border ${getCategoryColor(majorCategory)}`}>
                      <span className="hidden sm:inline">{majorCategory}</span>
                      <span className="sm:hidden">{majorCategory.split(' ')[0]}</span>
                    </div>
                  )}
                  {/* Admin Edit Link - Overlay on image */}
                  {isLoggedIn && (
                    <a
                      href={`/admin/gallery?designId=${design.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/admin/gallery?designId=${design.id}`);
                      }}
                      className="absolute top-2 right-2 bg-gray-700 bg-opacity-80 text-white p-1 rounded-full hover:bg-opacity-100 transition-colors shadow-lg z-10"
                      title="Edit in Admin"
                    >
                      <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </a>
                  )}
                  {/* Love Count - Bottom right */}
                  {design.loves !== undefined && design.loves > 0 && (
                    <div className="absolute bottom-1 right-1 md:bottom-2 md:right-2 flex items-center gap-0.5 md:gap-1 px-1.5 py-0.5 md:px-2 md:py-1 bg-white bg-opacity-90 text-gray-800 rounded-full text-xs font-medium shadow-md z-10">
                      <svg className="w-3.5 h-3.5 md:w-4 md:h-4 text-slate-500" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                      <span className="text-xs md:text-sm">{design.loves}</span>
                    </div>
                  )}
                </div>
                <div className="p-2 sm:p-4">
                  <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 mb-1">
                    {design.title}
                  </h3>
                  {design.description && (
                    <div 
                      className="text-xs sm:text-sm text-gray-600 line-clamp-2 mb-2 [&_a]:underline [&_a]:text-blue-600 [&_a:hover]:text-blue-800 [&_strong]:font-semibold [&_em]:italic"
                      dangerouslySetInnerHTML={{ __html: design.description }}
                    />
                  )}
                  {/* Tag Chips - Fewer on mobile */}
                  {safeMinorTags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {safeMinorTags.slice(0, 3).map((tag, idx) => (
                        <button
                          key={tag}
                          onClick={(e) => handleTagClick(tag, e)}
                          className={`px-1.5 py-0.5 md:px-2 md:py-0.5 text-xs bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors ${idx >= 2 ? 'hidden sm:inline-block' : ''}`}
                        >
                          {tag}
                        </button>
                      ))}
                      {safeMinorTags.length > 3 && (
                        <span className="px-1.5 py-0.5 text-xs text-gray-500">
                          +{safeMinorTags.length - 3}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {filteredDesigns.length === 0 && !loading && (
          <div className="text-center py-12">
            {searchQuery.trim() || orientationFilter || selectedTags.length > 0 ? (
              <div>
                <p className="text-gray-500 text-lg mb-2">
                  No designs found
                  {searchQuery.trim() && ` matching "${searchQuery}"`}
                  {orientationFilter && ` with ${orientationFilter} orientation`}
                  {selectedTags.length > 0 && ` with tag${selectedTags.length > 1 ? 's' : ''} "${selectedTags.join(', ')}"`}
                </p>
                <div className="flex gap-4 justify-center">
                  {searchQuery.trim() && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="text-blue-600 hover:text-blue-700 underline"
                    >
                      Clear search
                    </button>
                  )}
                  {orientationFilter && (
                    <button
                      onClick={() => setOrientationFilter(null)}
                      className="text-blue-600 hover:text-blue-700 underline"
                    >
                      Clear orientation
                    </button>
                  )}
                  {selectedTags.length > 0 && (
                    <button
                      onClick={() => navigate('/t/all')}
                      className="text-blue-600 hover:text-blue-700 underline"
                    >
                      Clear tag filter
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-gray-500 text-lg">No designs available</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default GalleryPage;
